/**
 * Markdown ⇄ page-engine blocks.
 *
 * A page body (a platform update, a page document) is a tree of blocks whose
 * text blocks carry HTML. That is the right storage shape and a hopeless
 * authoring shape for anything that is not the editor — an assistant writing
 * a post should not be composing `<p>` tags, and a model asked for raw block
 * JSON gets it subtly wrong.
 *
 * So: markdown in, blocks out, using the same Markdown subset the Rich Text
 * element already supports (headings, lists, bold, italic, links). Anything
 * outside that subset survives as plain text rather than being mangled.
 *
 * Lives in forms-core because it owns EacoDocumentContent and the parser;
 * apps/web, apps/public-api and anything else that grows a non-editor author
 * share one implementation rather than three that disagree about what a
 * paragraph is.
 */
import { parseMarkdown, type MdInline } from "./markdown";
import type { EacoDocumentContent } from "./types";

const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** An href we are willing to put in an anchor. */
export const safeHref = (href: string): string | null => {
  const trimmed = href.trim();
  // Relative links are fine; of the schemes, only the ones that navigate.
  if (/^(https?:|mailto:|tel:)/i.test(trimmed)) return trimmed;
  if (/^[/#]/.test(trimmed)) return trimmed;
  // javascript:, data: and friends never reach the document.
  return null;
};

const inlineToHtml = (run: MdInline): string => {
  switch (run.type) {
    case "bold":
      return `<strong>${escapeHtml(run.value)}</strong>`;
    case "italic":
      return `<em>${escapeHtml(run.value)}</em>`;
    case "code":
      return `<code>${escapeHtml(run.value)}</code>`;
    case "link": {
      const href = safeHref(run.href);
      const text = escapeHtml(run.value);
      // A refused href keeps its text: losing the words would be a worse
      // surprise than losing the link.
      return href ? `<a href="${escapeHtml(href)}">${text}</a>` : text;
    }
    default:
      return escapeHtml(run.value);
  }
};

const inlines = (runs: MdInline[]): string => runs.map(inlineToHtml).join("");

/** The editor's own block types; a converted document must look like a typed one. */
const TEXT_TYPE = "page-text";
const BULLET_TYPE = "page-bullet-list";
const NUMBERED_TYPE = "page-numbered-list";
const TODO_TYPE = "page-todo-list";
const QUOTE_TYPE = "page-quote";
const CODE_TYPE = "page-code";
const TABLE_TYPE = "page-table";
const IMAGE_TYPE = "page-image";
const DIVIDER_TYPE = "page-divider";
const headingType = (level: number) =>
  `page-heading-${Math.min(Math.max(level, 1), 4)}`;

const plain = (runs: MdInline[]): string => runs.map((r) => r.value).join("");

/**
 * Markdown to a page body.
 *
 * One block per Markdown block, of the type the editor itself would have
 * made: a heading is a page-heading-N block (plain text, the editor's own
 * range of 1–4), each list item is its own page-bullet-list block, and a
 * paragraph is a page-text block holding its inline HTML. Earlier this put
 * the whole document into one text block as raw <h1>/<ul> markup, which
 * the read-only renderer does not style: every heading came out as a
 * paragraph and every list lost its bullets.
 */
export function markdownToBlocks(markdown: string): EacoDocumentContent {
  const elements: Array<Record<string, unknown>> = [];
  const id = () => `page-md-${elements.length + 1}`;
  for (const block of parseMarkdown(markdown ?? "")) {
    switch (block.type) {
      case "heading": {
        const level = Math.min(Math.max(block.level, 1), 4);
        elements.push({
          id: id(),
          name: `Heading ${level}`,
          typeId: headingType(level),
          // The catalogue seeds level beside text; the renderer reads the
          // type id first and falls back to it for legacy content.
          data: { text: plain(block.inlines), level: String(level) },
        });
        break;
      }
      case "list":
        for (const item of block.items) {
          elements.push({
            id: id(),
            name: "Bulleted list",
            typeId: BULLET_TYPE,
            data: { content: inlines(item) },
          });
        }
        break;
      case "ordered-list":
        // One element per item, like bullets: the editor numbers a run of
        // consecutive blocks itself rather than storing an index.
        for (const item of block.items) {
          elements.push({
            id: id(),
            name: "Numbered list",
            typeId: NUMBERED_TYPE,
            data: { content: inlines(item) },
          });
        }
        break;
      case "todo":
        for (const item of block.items) {
          elements.push({
            id: id(),
            name: "To-do list",
            typeId: TODO_TYPE,
            data: { content: inlines(item.inlines), checked: item.checked },
          });
        }
        break;
      case "table":
        // Cells are plain text in the stored block, so no inline HTML here
        // — the editor's table does not render marks inside a cell.
        elements.push({
          id: id(),
          name: "Table",
          typeId: TABLE_TYPE,
          data: { columns: block.columns, rows: block.rows },
        });
        break;
      case "image": {
        // A refused href keeps nothing: an image block with no src is an
        // empty box, so the alt text becomes a paragraph instead.
        const href = safeHref(block.url);
        if (href) {
          elements.push({
            id: id(),
            name: "Image",
            typeId: IMAGE_TYPE,
            data: { imageUrl: href, alt: block.alt },
          });
        } else if (block.alt) {
          elements.push({
            id: id(),
            name: "Text",
            typeId: TEXT_TYPE,
            data: { content: `<p>${escapeHtml(block.alt)}</p>` },
          });
        }
        break;
      }
      case "divider":
        elements.push({ id: id(), name: "Divider", typeId: DIVIDER_TYPE, data: {} });
        break;
      case "quote":
        elements.push({
          id: id(),
          name: "Quote",
          typeId: QUOTE_TYPE,
          data: { content: `<p>${inlines(block.inlines)}</p>` },
        });
        break;
      case "code":
        // Plain text with real newlines, NOT inline HTML: a snippet whose
        // angle brackets were escaped into entities is not the snippet.
        elements.push({
          id: id(),
          name: "Code",
          typeId: CODE_TYPE,
          data: { content: block.text, ...(block.lang ? { language: block.lang } : {}) },
        });
        break;
      default:
        elements.push({
          id: id(),
          name: "Text",
          typeId: TEXT_TYPE,
          data: { content: `<p>${inlines(block.inlines)}</p>` },
        });
    }
  }
  if (elements.length === 0) {
    elements.push({
      id: "page-first-text",
      name: "Text",
      typeId: TEXT_TYPE,
      data: { content: "<p></p>" },
    });
  }
  return { elements: elements as EacoDocumentContent["elements"] };
}

/**
 * The same Markdown as plain text, for excerpts and search.
 *
 * Derived from the parsed AST rather than by stripping the HTML we just
 * built, and never from the raw source: a feed excerpt reading
 * "# What changed - **publish** dialog" shows the author's syntax to every
 * reader, which is exactly what a plain-text field exists to avoid.
 */
export function markdownToText(markdown: string): string {
  const runs = (inlines: MdInline[]) => inlines.map((i) => i.value).join("");
  return parseMarkdown(markdown ?? "")
    .map((block) =>
      block.type === "list"
        ? block.items.map(runs).join(" ")
        : runs((block as { inlines: MdInline[] }).inlines),
    )
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .join(" ");
}

/* -------------------------------------------------------------------------
 * Blocks back to Markdown.
 *
 * The other direction, which nothing had. A reader that is not the editor —
 * an assistant asked what a page says — otherwise gets one of three bad
 * answers: the raw block JSON (apps/ai-worker shows the model minified
 * elements), HTML (the web clipboard serialiser), or every structure
 * flattened into one space-joined line (apps/web blocksToText, where a
 * heading and a paragraph are indistinguishable).
 *
 * Markdown keeps the structure, costs a tenth of the tokens of the JSON,
 * and round-trips through markdownToBlocks for the block types both
 * directions share — which the tests pin.
 * ---------------------------------------------------------------------- */

/** Inline HTML back to the Markdown that produced it. */
function htmlToMarkdown(html: string): string {
  return (
    String(html ?? "")
      // Block tags the editor may have left around the run.
      .replace(/<\/?(p|div)[^>]*>/gi, "")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<(strong|b)>([\s\S]*?)<\/\1>/gi, "**$2**")
      .replace(/<(em|i)>([\s\S]*?)<\/\1>/gi, "*$2*")
      .replace(/<code>([\s\S]*?)<\/code>/gi, "`$1`")
      .replace(/<a\s[^>]*href="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi, "[$2]($1)")
      // Anything else the editor can store but this subset cannot express.
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      // Last, or it would un-escape the entities above twice.
      .replace(/&amp;/g, "&")
      .trim()
  );
}

const str = (v: unknown): string => (typeof v === "string" ? v : "");

/**
 * A page body as Markdown.
 *
 * Unknown block types are not dropped: anything with readable `content` or
 * `text` becomes a paragraph, so a page using a block this subset does not
 * model still reads as prose rather than losing a section silently. Blocks
 * with neither (a divider, an empty container) contribute nothing.
 */
export function blocksToMarkdown(content: unknown): string {
  const root = (content ?? {}) as { elements?: unknown };
  const out: string[] = [];

  const walk = (elements: unknown[], depth: number): void => {
    for (const raw of elements ?? []) {
      const el = (raw ?? {}) as {
        typeId?: string;
        data?: Record<string, unknown>;
        elements?: unknown[];
        archived?: boolean;
      };
      if (el.archived) continue;
      const data = el.data ?? {};
      const typeId = String(el.typeId ?? "");
      const indent = "  ".repeat(Math.max(0, depth));

      const heading = /^page-heading-([1-4])$/.exec(typeId);
      if (heading) {
        // Headings store plain text in `text`, not HTML in `content`.
        const text = str(data.text) || htmlToMarkdown(str(data.content));
        out.push(`${"#".repeat(Number(heading[1]))} ${text}`.trim());
      } else if (typeId === BULLET_TYPE) {
        out.push(`${indent}- ${htmlToMarkdown(str(data.content))}`);
      } else if (typeId === NUMBERED_TYPE) {
        // Always "1." — Markdown renumbers, and the stored blocks carry no
        // index of their own to be faithful to.
        out.push(`${indent}1. ${htmlToMarkdown(str(data.content))}`);
      } else if (typeId === TODO_TYPE || typeId === "page-action-item") {
        const box = data.checked ? "x" : " ";
        out.push(`${indent}- [${box}] ${htmlToMarkdown(str(data.content))}`);
      } else if (typeId === QUOTE_TYPE) {
        out.push(`> ${htmlToMarkdown(str(data.content))}`);
      } else if (typeId === CODE_TYPE) {
        const lang = str(data.language);
        out.push(`\`\`\`${lang}\n${str(data.content)}\n\`\`\``);
      } else if (typeId === DIVIDER_TYPE) {
        out.push("---");
      } else if (typeId === "page-table") {
        const columns = Array.isArray(data.columns) ? data.columns.map(str) : [];
        const rows = Array.isArray(data.rows) ? (data.rows as unknown[]) : [];
        if (columns.length) {
          // One entry, not one per row: a Markdown table's rows are
          // separated by a single newline, and the joiner below puts a
          // blank line between entries.
          out.push(
            [
              `| ${columns.join(" | ")} |`,
              `| ${columns.map(() => "---").join(" | ")} |`,
              ...rows.map((row) => `| ${(Array.isArray(row) ? row.map(str) : []).join(" | ")} |`),
            ].join("\n"),
          );
        }
      } else if (typeId === "page-image") {
        const url = str(data.imageUrl);
        if (url) out.push(`![${str(data.alt)}](${url})`);
      } else {
        // page-text, and every block this subset does not model.
        const text = htmlToMarkdown(str(data.content)) || str(data.text).trim();
        if (text) out.push(text);
      }

      // Containers (callout, card, page) hold their children in elements[].
      if (Array.isArray(el.elements) && el.elements.length) {
        walk(el.elements, typeId === "pages" || typeId === "page" ? depth : depth + 1);
      }
    }
  };

  walk(Array.isArray(root.elements) ? root.elements : [], 0);

  // One blank line between blocks, but items of the SAME list stay together
  // so a list reads as a list. Same list, not merely both list-shaped: a
  // bullet run followed by a numbered one needs the blank line, or Markdown
  // reads the second as a continuation of the first and the numbering is
  // lost on the way back in.
  const kindOf = (line: string): string | null => {
    if (/^\s*[-*]\s+\[[ xX]\]\s/.test(line)) return "todo";
    if (/^\s*[-*]\s/.test(line)) return "bullet";
    if (/^\s*\d+[.)]\s/.test(line)) return "ordered";
    return null;
  };
  return out
    .reduce<string[]>((acc, line, i) => {
      if (i > 0) {
        const kind = kindOf(line);
        acc.push(kind !== null && kind === kindOf(out[i - 1]!) ? "\n" : "\n\n");
      }
      acc.push(line);
      return acc;
    }, [])
    .join("")
    .trim();
}
